import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { useEffect, useMemo, useState } from "react"
import type { CompanyProfileLookupFromGUSResponse } from "../../api/companyProfile"
import type {
  CompanyProfileDraft,
  CompanyProfileField,
} from "./companyProfileForm"

type GUSCompanyProfileField = Exclude<CompanyProfileField, "labelLogoSource">

type GUSField = {
  field: GUSCompanyProfileField
  label: string
  currentValue: string
  nextValue: string
}

type Props = {
  isOpen: boolean
  result: CompanyProfileLookupFromGUSResponse | null
  draft: CompanyProfileDraft
  onClose: () => void
  onApply: (values: Partial<CompanyProfileDraft>) => void
}

export default function CompanyProfileGUSDialog({
  isOpen,
  result,
  draft,
  onClose,
  onApply,
}: Props) {
  const changedBg = useColorModeValue("green.50", "green.900")
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200")
  const fields = useMemo<GUSField[]>(() => {
    if (!result) return []
    return [
      [
        "legalName",
        "Nazwa prawna",
        draft.legalName,
        result.prefill.legalName ?? "",
      ],
      ["taxId", "NIP", draft.taxId, result.prefill.taxId ?? ""],
      ["regon", "REGON", draft.regon, result.prefill.regon ?? ""],
      ["krs", "KRS", draft.krs, result.prefill.krs ?? ""],
      ["bdo", "BDO", draft.bdo, result.prefill.bdo ?? ""],
      [
        "addressLine1",
        "Adres — wiersz 1",
        draft.addressLine1,
        result.prefill.addressLine1 ?? "",
      ],
      [
        "addressLine2",
        "Adres — wiersz 2",
        draft.addressLine2,
        result.prefill.addressLine2 ?? "",
      ],
      [
        "postalCode",
        "Kod pocztowy",
        draft.postalCode,
        result.prefill.postalCode ?? "",
      ],
      ["city", "Miasto", draft.city, result.prefill.city ?? ""],
      [
        "countryCode",
        "Kod kraju",
        draft.countryCode,
        result.prefill.countryCode ?? "",
      ],
    ].map(([field, label, currentValue, nextValue]) => ({
      field: field as GUSCompanyProfileField,
      label,
      currentValue: currentValue ?? "",
      nextValue: nextValue ?? "",
    }))
  }, [draft, result])
  const defaultSelected = useMemo(
    () =>
      fields
        .filter(
          (field) =>
            field.nextValue.trim() &&
            field.nextValue.trim() !== field.currentValue.trim(),
        )
        .map((field) => field.field),
    [fields],
  )
  const [selected, setSelected] = useState<GUSCompanyProfileField[]>([])

  useEffect(() => {
    if (isOpen) setSelected(defaultSelected)
  }, [defaultSelected, isOpen])

  const apply = () => {
    const values: Partial<CompanyProfileDraft> = {}
    for (const field of fields) {
      if (selected.includes(field.field) && field.nextValue.trim()) {
        values[field.field] = field.nextValue
      }
    }
    onApply(values)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        <ModalHeader>
          <Stack spacing={2} align="start">
            <Text>Porównaj dane z GUS</Text>
            {result ? (
              <Flex gap={2} wrap="wrap">
                <Badge
                  colorScheme={result.mocked ? "orange" : "teal"}
                  borderRadius="full"
                  px={3}
                >
                  {result.mocked ? "Tryb testowy" : "Dane oficjalne"}
                </Badge>
                <Badge colorScheme="blue" borderRadius="full" px={3}>
                  Wybrano: {selected.length}
                </Badge>
              </Flex>
            ) : null}
          </Stack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {result ? (
            <Stack spacing={4}>
              <Box>
                <Text fontWeight="bold">{result.companyName}</Text>
                <Text fontSize="sm" color="gray.500">
                  Zaznacz tylko pola, które chcesz przenieść do formularza.
                  Zapis nastąpi później.
                </Text>
              </Box>
              <Stack spacing={3}>
                {fields.map((field) => {
                  const changed =
                    field.currentValue.trim() !== field.nextValue.trim()
                  const selectable = changed && Boolean(field.nextValue.trim())
                  return (
                    <Box
                      key={field.field}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="xl"
                      p={3}
                      bg={changed ? changedBg : undefined}
                    >
                      <Checkbox
                        isChecked={selected.includes(field.field)}
                        isDisabled={!selectable}
                        onChange={(event) =>
                          setSelected((current) =>
                            event.target.checked
                              ? [...current, field.field]
                              : current.filter((item) => item !== field.field),
                          )
                        }
                      >
                        <Text fontWeight="semibold">{field.label}</Text>
                      </Checkbox>
                      <Flex
                        ml={{ base: 0, sm: 6 }}
                        mt={2}
                        gap={4}
                        direction={{ base: "column", sm: "row" }}
                      >
                        <Box flex="1" minW={0}>
                          <Text fontSize="xs" color="gray.500">
                            OBECNIE
                          </Text>
                          <Text wordBreak="break-word">
                            {field.currentValue || "—"}
                          </Text>
                        </Box>
                        <Box flex="1" minW={0}>
                          <Text fontSize="xs" color="gray.500">
                            GUS
                          </Text>
                          <Text
                            fontWeight={changed ? "semibold" : "normal"}
                            wordBreak="break-word"
                          >
                            {field.nextValue || "—"}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  )
                })}
              </Stack>
              {result.mockNote ? (
                <Text color="orange.500" fontSize="sm">
                  {result.mockNote}
                </Text>
              ) : null}
            </Stack>
          ) : null}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            colorScheme="teal"
            isDisabled={selected.length === 0}
            onClick={apply}
          >
            Zastosuj wybrane pola ({selected.length})
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
